import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Restaurant,
  Coffee,
  Wc,
  Stop,
  AccessTime,
  Language,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { timeEntriesAPI, schedulesAPI, apiClient } from '../services/api';
import { ClockStatus, CampaignWithSchedule, User } from '../types';
import { isScheduleValidationExemptRole } from '../utils/userVisibilityPolicy';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

const ClockInOutPage: React.FC = () => {
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [clockOutDialogOpen, setClockOutDialogOpen] = useState(false);
  const [clockInReason, setClockInReason] = useState<'covering_employee' | 'jumping_into_operations' | 'scheduled'>('scheduled');
  const [coveredEmployeeId, setCoveredEmployeeId] = useState<string>('');
  const [coveredEmployeeSearch, setCoveredEmployeeSearch] = useState('');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { showTimezoneDebug, clockOutConfirmationEnabled } = useSettings();
  
  // Supervisors/managers/backup + privileged roles may clock in without a published schedule
  const isSupervisorOrAbove = Boolean(
    user?.role
    && (['supervisor', 'manager', 'backup'].includes(user.role)
      || isScheduleValidationExemptRole(user.role)),
  );
  const isAgentClockInUser = user?.role === 'agent' || user?.role === 'team_lead';

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch current status
  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['clockStatus'],
    queryFn: () => timeEntriesAPI.getStatus(),
    refetchInterval: 15000, // Refetch every 15 seconds
  });

  // Fetch available campaigns based on user's current schedule
  const { data: campaignsData, isLoading: campaignsLoading, error: campaignsError } = useQuery({
    queryKey: ['availableCampaigns'],
    queryFn: () => timeEntriesAPI.getAvailableCampaigns(),
    refetchInterval: 10000, // Refetch every 10 seconds to quickly show available campaigns when schedule starts
  });

  // Fetch today's schedules (regardless of time availability)
  const { data: todaySchedulesData, isLoading: todaySchedulesLoading } = useQuery({
    queryKey: ['todaySchedules'],
    queryFn: () => schedulesAPI.getMyTodaySchedule(),
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch all active users for employee coverage dropdown (only for supervisors/managers/backup)
  const { data: usersData } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => apiClient.users.getAll(),
    enabled: isSupervisorOrAbove && clockInReason === 'covering_employee',
  });

  const availableUsers: User[] = usersData?.data?.data?.filter((u: User) => 
    u.isActive && u.id !== user?.id
  ) || [];

  const { data: coverageBlockData } = useQuery({
    queryKey: ['clockInCoverageBlock', selectedCampaign, user?.id],
    queryFn: () => timeEntriesAPI.getCoverageBlock(selectedCampaign),
    enabled: Boolean(isAgentClockInUser && selectedCampaign && statusData?.data?.data?.status === 'clocked_out'),
    refetchInterval: 15000,
  });

  const coverageBlock = coverageBlockData?.data?.data;

  // Debug logging for available campaigns
  useEffect(() => {
    console.log('🔍 Available campaigns debug:', {
      campaignsData,
      campaigns: campaignsData?.data || [],
      campaignsLoading,
      campaignsError
    });
  }, [campaignsData, campaignsLoading, campaignsError]);

  // Debug logging for today's schedules
  useEffect(() => {
    const schedules = todaySchedulesData?.data?.data || [];
    console.log('📅 Today\'s schedules debug:', {
      todaySchedulesData,
      schedules,
      todaySchedulesLoading,
      dataStructure: todaySchedulesData ? Object.keys(todaySchedulesData) : 'no data'
    });
    
    // Log individual schedule details
    if (Array.isArray(schedules) && schedules.length > 0) {
      schedules.forEach((schedule, index) => {
        console.log(`📅 Schedule ${index}:`, {
          id: schedule.id,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          startTimeLocal: schedule.startTimeLocal,
          endTimeLocal: schedule.endTimeLocal,
          startTimeUTC: schedule.startTimeUTC,
          endTimeUTC: schedule.endTimeUTC,
          userTimezone: schedule.userTimezone,
          campaign: schedule.campaign
        });
      });
    }
  }, [todaySchedulesData, todaySchedulesLoading]);

  // Smart refresh: Check if we're close to a schedule start time and refresh more frequently
  useEffect(() => {
    if (!todaySchedulesData?.data?.data || !Array.isArray(todaySchedulesData.data.data) || todaySchedulesData.data.data.length === 0) return;

    const now = new Date();
    const userTimezone = user?.timezone || 'UTC';
    
    // Check if any schedule is starting within the next 5 minutes
    const upcomingSchedule = todaySchedulesData.data.data.find(schedule => {
      try {
        // Parse the schedule start time
        const [hours, minutes] = schedule.startTime.split(':').map(Number);
        const scheduleStart = new Date();
        scheduleStart.setHours(hours, minutes, 0, 0);
        
        // Convert to user's timezone for comparison
        const timeDiff = scheduleStart.getTime() - now.getTime();
        return timeDiff > 0 && timeDiff <= 5 * 60 * 1000; // Within next 5 minutes
      } catch (error) {
        return false;
      }
    });

    if (upcomingSchedule) {
      // If we're close to a schedule start, refresh more frequently
      const refreshTimer = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['availableCampaigns'] });
      }, 5000); // Refresh every 5 seconds when close to schedule start

      return () => clearInterval(refreshTimer);
    }
  }, [todaySchedulesData, user?.timezone, queryClient]);

  const clockStatus: ClockStatus = statusData?.data.data || { status: 'clocked_out' };
  const campaigns: CampaignWithSchedule[] = campaignsData?.data?.data || [];
  // For supervisors/managers/backup: campaigns include all active campaigns (not just scheduled)
  // For regular users: only campaigns from available-campaigns endpoint (validated for time window)
  const mergedCampaignOptions: { id: string; name: string }[] = (campaigns || []).map(c => ({ 
    id: (c as any).id, 
    name: (c as any).name 
  }));

  // Auto-select scheduled campaign for supervisors/managers/backup, or first available for others
  useEffect(() => {
    if (!selectedCampaign && mergedCampaignOptions.length > 0) {
      if (isSupervisorOrAbove) {
        // For supervisors/managers/backup: pre-select the campaign they're scheduled for (if any)
        const scheduledCampaign = campaigns.find(c => c.schedule);
        if (scheduledCampaign) {
          setSelectedCampaign(scheduledCampaign.id as any);
        } else if (mergedCampaignOptions.length === 1) {
          // Fallback to first option if no scheduled campaign
          setSelectedCampaign(mergedCampaignOptions[0].id as any);
        }
      } else {
        // For regular users: auto-select if only one option
        if (mergedCampaignOptions.length === 1) {
          setSelectedCampaign(mergedCampaignOptions[0].id as any);
        }
      }
    }
  }, [mergedCampaignOptions, selectedCampaign, isSupervisorOrAbove, campaigns]);

  // Show error message if campaigns failed to load
  useEffect(() => {
    if (campaignsError) {
      console.error('❌ Failed to load available campaigns:', campaignsError);
      setMessage('Failed to load available campaigns. Please refresh the page.');
      setMessageType('error');
    }
  }, [campaignsError]);

  // Helper function to format timezone display
  const formatTimezoneDisplay = (timezone: string) => {
    if (!timezone) return '';
    const parts = timezone.split('/');
    if (parts.length > 1) {
      return parts[parts.length - 1].replace(/_/g, ' ');
    }
    return timezone;
  };

  // Clock in mutation
  const clockInMutation = useMutation({
    mutationFn: timeEntriesAPI.clockIn,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setSelectedCampaign('');
      setNotes('');
      setClockInReason('scheduled');
      setCoveredEmployeeId('');
      setCoveredEmployeeSearch('');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to clock in';
      setMessage(errorMessage);
      setMessageType('error');
    },
  });

  // Clock out mutation
  const clockOutMutation = useMutation({
    mutationFn: timeEntriesAPI.clockOut,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to clock out';
      setMessage(errorMessage);
      setMessageType('error');
    },
  });

  // Break start mutation
  const breakStartMutation = useMutation({
    mutationFn: timeEntriesAPI.breakStart,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to start break');
      setMessageType('error');
    },
  });

  // Break end mutation
  const breakEndMutation = useMutation({
    mutationFn: timeEntriesAPI.breakEnd,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to end break');
      setMessageType('error');
    },
  });

  // Lunch start mutation
  const lunchStartMutation = useMutation({
    mutationFn: timeEntriesAPI.lunchStart,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to start lunch');
      setMessageType('error');
    },
  });

  // Lunch end mutation
  const lunchEndMutation = useMutation({
    mutationFn: timeEntriesAPI.lunchEnd,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to end lunch');
      setMessageType('error');
    },
  });

  // Bathroom start mutation
  const bathroomStartMutation = useMutation({
    mutationFn: timeEntriesAPI.bathroomStart,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to start bathroom');
      setMessageType('error');
    },
  });

  // Bathroom end mutation
  const bathroomEndMutation = useMutation({
    mutationFn: timeEntriesAPI.bathroomEnd,
    onSuccess: (response) => {
      setMessage(response.data.message);
      setMessageType('success');
      queryClient.invalidateQueries({ queryKey: ['clockStatus'] });
      setNotes('');
    },
    onError: (error: any) => {
      setMessage(error.response?.data?.message || 'Failed to end bathroom');
      setMessageType('error');
    },
  });

  const handleClockIn = () => {
    // Only use selectedCampaign - no fallback to prevent bypassing schedule validation
    if (!selectedCampaign) {
      setMessage('Please select a campaign');
      setMessageType('error');
      return;
    }

    // Validate coverage employee selection
    if (isSupervisorOrAbove && clockInReason === 'covering_employee' && !coveredEmployeeId) {
      setMessage('Please select an employee to cover');
      setMessageType('error');
      return;
    }

    if (coverageBlock?.isBlocked) {
      setMessage(coverageBlock.message || 'You have been covered for this shift and cannot clock in.');
      setMessageType('error');
      return;
    }

    const clockInData: any = {
      campaignId: selectedCampaign,
      notes: notes || undefined,
      clockInReason: isSupervisorOrAbove ? clockInReason : 'scheduled',
    };
    
    // Only include coveredEmployeeId if covering employee
    if (isSupervisorOrAbove && clockInReason === 'covering_employee' && coveredEmployeeId) {
      clockInData.coveredEmployeeId = coveredEmployeeId;
    }
    
    clockInMutation.mutate(clockInData);
  };

  const handleClockOut = () => {
    if (clockOutConfirmationEnabled) {
      setClockOutDialogOpen(true);
    } else {
      // If confirmation is disabled, clock out directly
      clockOutMutation.mutate({ notes });
    }
  };

  const handleClockOutConfirm = () => {
    setClockOutDialogOpen(false);
    clockOutMutation.mutate({ notes });
  };

  const handleClockOutCancel = () => {
    setClockOutDialogOpen(false);
  };

  const handleBreakStart = () => {
    breakStartMutation.mutate({ notes });
  };

  const handleBreakEnd = () => {
    breakEndMutation.mutate({ notes });
  };

  const handleLunchStart = () => {
    lunchStartMutation.mutate({ notes });
  };

  const handleLunchEnd = () => {
    lunchEndMutation.mutate({ notes });
  };

  const handleBathroomStart = () => {
    bathroomStartMutation.mutate({ notes });
  };

  const handleBathroomEnd = () => {
    bathroomEndMutation.mutate({ notes });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'clocked_in':
        return 'success';
      case 'on_break':
        return 'warning';
      case 'on_lunch':
        return 'info';
      case 'on_bathroom':
        return 'secondary';
      case 'clocked_out':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'clocked_in':
        return 'Clocked In';
      case 'on_break':
        return 'On Break';
      case 'on_lunch':
        return 'On Lunch';
      case 'on_bathroom':
        return 'On Bathroom';
      case 'clocked_out':
        return 'Clocked Out';
      default:
        return 'Unknown';
    }
  };

  const isLoading = clockInMutation.isPending || clockOutMutation.isPending || 
                   breakStartMutation.isPending || breakEndMutation.isPending ||
                   lunchStartMutation.isPending || lunchEndMutation.isPending ||
                   bathroomStartMutation.isPending || bathroomEndMutation.isPending;

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Clock In/Out
      </Typography>
      
      {/* Live Date and Time Display */}
      <Card sx={{ 
        mb: 3, 
        backgroundColor: '#f8f9fa',
        border: '2px solid #e3f2fd',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
      }}>
        <CardContent>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              mb: 1
            }}>
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
            <Typography variant="h4" sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              fontFamily: 'monospace',
              letterSpacing: '2px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
            }}>
              {currentTime.toLocaleTimeString('en-US', { 
                hour12: true, 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              mt: 1,
              fontStyle: 'italic'
            }}>
              Local Time
            </Typography>
          </Box>
        </CardContent>
      </Card>


      {message && (
        <Alert 
          severity={messageType} 
          sx={{ mb: 2 }}
          onClose={() => setMessage('')}
        >
          {message}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Status Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Status
            </Typography>
            {statusLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Chip
                  label={getStatusText(clockStatus.status)}
                  color={getStatusColor(clockStatus.status) as any}
                  size="medium"
                  sx={{ mb: 2, fontSize: '1.1rem', height: '36px' }}
                />
                {clockStatus.currentCampaign && (
                  <Typography variant="body2" color="text.secondary">
                    Campaign: {clockStatus.currentCampaign.name}
                  </Typography>
                )}
                {clockStatus.lastEntry && (
                  <Typography variant="body2" color="text.secondary">
                    Last action: {new Date(clockStatus.lastEntry.timestamp).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
              
              {clockStatus.status === 'clocked_out' && (
                <Box>
                  {campaignsLoading ? (
                    <Box display="flex" alignItems="center" gap={2} sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography>Loading available campaigns...</Typography>
                    </Box>
                  ) : mergedCampaignOptions.length === 0 ? (
                    <Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {isSupervisorOrAbove
                          ? 'No campaigns are available. Please contact your administrator.'
                          : 'No campaigns are available at this time. You can only clock in during your scheduled hours. Please check your schedule or contact your supervisor.'}
                      </Alert>
                      {!isSupervisorOrAbove
                        && Array.isArray(todaySchedulesData?.data?.data)
                        && (todaySchedulesData?.data?.data?.length ?? 0) > 0 && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Your schedule today
                          </Typography>
                          {((todaySchedulesData?.data?.data ?? []) as any[]).map((schedule: any) => (
                            <Box key={schedule.id} sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {schedule.campaign?.name || 'Scheduled shift'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {schedule.startTimeLocal} &ndash; {schedule.endTimeLocal}
                                {schedule.userTimezone ? ` (${formatTimezoneDisplay(schedule.userTimezone)})` : ''}
                              </Typography>
                            </Box>
                          ))}
                          <Typography variant="caption" color="text.secondary">
                            Clock-in opens shortly before your shift start time. The button will
                            enable automatically when your scheduled window begins.
                          </Typography>
                        </Paper>
                      )}
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                        <CircularProgress size={16} />
                        <Typography variant="caption" color="text.secondary">
                          Auto-refreshing every 10 seconds to check for available campaigns...
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Select Campaign</InputLabel>
                        <Select
                          value={selectedCampaign}
                          onChange={(e) => setSelectedCampaign(e.target.value)}
                          disabled={isLoading}
                        >
                          {mergedCampaignOptions.map((opt) => (
                            <MenuItem key={opt.id} value={opt.id}>
                              {opt.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {isAgentClockInUser && coverageBlock?.isBlocked && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          {coverageBlock.message || 'You have been covered for this shift and cannot clock in.'}
                        </Alert>
                      )}

                      {/* Clock-in reason selection for supervisors/managers/backup */}
                      {isSupervisorOrAbove && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Clock-In Reason
                          </Typography>
                          <RadioGroup
                            value={clockInReason}
                            onChange={(e) => {
                              setClockInReason(e.target.value as any);
                              if (e.target.value !== 'covering_employee') {
                                setCoveredEmployeeId('');
                                setCoveredEmployeeSearch('');
                              }
                            }}
                          >
                            <FormControlLabel 
                              value="scheduled" 
                              control={<Radio />} 
                              label={
                                selectedCampaign 
                                  ? `I am scheduled for this campaign (${mergedCampaignOptions.find(c => c.id === selectedCampaign)?.name || ''})`
                                  : "I am scheduled for this campaign"
                              }
                            />
                            <FormControlLabel 
                              value="jumping_into_operations" 
                              control={<Radio />} 
                              label={
                                selectedCampaign 
                                  ? `I am assisting with operations (${mergedCampaignOptions.find(c => c.id === selectedCampaign)?.name || ''}) as ${user?.displayName || `${user?.firstName} ${user?.lastName}`}`
                                  : `I am assisting with operations as ${user?.displayName || `${user?.firstName} ${user?.lastName}`}`
                              }
                            />
                            <FormControlLabel 
                              value="covering_employee" 
                              control={<Radio />} 
                              label="I am covering for another employee" 
                            />
                          </RadioGroup>

                          {/* Employee selection dropdown when covering */}
                          {clockInReason === 'covering_employee' && (
                            <Box sx={{ mt: 2, ml: 4 }}>
                              <Autocomplete
                                options={availableUsers}
                                getOptionLabel={(option) => 
                                  option.displayName || `${option.firstName} ${option.lastName}`
                                }
                                value={availableUsers.find(u => u.id === coveredEmployeeId) || null}
                                onChange={(_, newValue) => {
                                  setCoveredEmployeeId(newValue?.id || '');
                                  setCoveredEmployeeSearch(newValue ? 
                                    (newValue.displayName || `${newValue.firstName} ${newValue.lastName}`) : ''
                                  );
                                }}
                                inputValue={coveredEmployeeSearch}
                                onInputChange={(_, newInputValue) => {
                                  setCoveredEmployeeSearch(newInputValue);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Select Employee"
                                    placeholder="Search for employee..."
                                    required
                                    error={!coveredEmployeeId}
                                    helperText={!coveredEmployeeId ? 'Employee selection is required' : ''}
                                  />
                                )}
                                disabled={isLoading}
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                    </Box>
                  )}
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrow />}
                    onClick={handleClockIn}
                    disabled={
                      isLoading || 
                      !selectedCampaign || 
                      campaigns.length === 0 ||
                      coverageBlock?.isBlocked ||
                      (isSupervisorOrAbove && clockInReason === 'covering_employee' && !coveredEmployeeId)
                    }
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Clock In
                  </Button>
                </Box>
              )}

              {clockStatus.status === 'clocked_in' && (
                <Box>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<Coffee />}
                        onClick={handleBreakStart}
                        disabled={isLoading}
                        sx={{ flex: 1 }}
                      >
                        Start Break
                      </Button>
                      <Button
                        variant="outlined"
                        color="info"
                        startIcon={<Restaurant />}
                        onClick={handleLunchStart}
                        disabled={isLoading}
                        sx={{ flex: 1 }}
                      >
                        Start Lunch
                      </Button>
                    </Box>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<Wc />}
                      onClick={handleBathroomStart}
                      disabled={isLoading}
                      fullWidth
                    >
                      Start Bathroom
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Stop />}
                      onClick={handleClockOut}
                      disabled={isLoading}
                      fullWidth
                    >
                      Clock Out
                    </Button>
                  </Box>
                </Box>
              )}

              {clockStatus.status === 'on_break' && (
                <Box>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<Pause />}
                    onClick={handleBreakEnd}
                    disabled={isLoading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    End Break
                  </Button>
                </Box>
              )}

              {clockStatus.status === 'on_bathroom' && (
                <Box>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Pause />}
                    onClick={handleBathroomEnd}
                    disabled={isLoading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    End Bathroom
                  </Button>
                </Box>
              )}

              {clockStatus.status === 'on_lunch' && (
                <Box>
                  <TextField
                    fullWidth
                    margin="normal"
                    label="Notes (optional)"
                    multiline
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={isLoading}
                  />
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<Pause />}
                    onClick={handleLunchEnd}
                    disabled={isLoading}
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    End Lunch
                  </Button>
                </Box>
              )}
          </CardContent>
        </Card>

      </Box>

      {/* Clock Out Confirmation Dialog */}
      <Dialog
        open={clockOutDialogOpen}
        onClose={handleClockOutCancel}
        aria-labelledby="clock-out-dialog-title"
        aria-describedby="clock-out-dialog-description"
      >
        <DialogTitle id="clock-out-dialog-title">
          Confirm Clock Out
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clock-out-dialog-description">
            Are you sure you want to clock out for the day{clockStatus.currentCampaign ? ` in ${clockStatus.currentCampaign.name}` : ''}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClockOutCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleClockOutConfirm} color="error" variant="contained" autoFocus>
            Clock Out
          </Button>
        </DialogActions>
      </Dialog>

      {/* Timezone Debugging Information Card - Moved to Bottom */}
      {showTimezoneDebug && (
        <Card sx={{ 
          mt: 3, 
          backgroundColor: '#fff3e0',
          border: '2px solid #ff9800',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
        }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AccessTime sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
              Timezone Debug Information
            </Typography>
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* Current Time Information */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                Current Time
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Local Time (Browser):
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {currentTime.toLocaleString('en-US', { 
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  UTC Time:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {currentTime.toLocaleString('en-US', { 
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                    timeZone: 'UTC'
                  })} UTC
                </Typography>
              </Box>
            </Box>

            {/* User Timezone Information */}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                User Configuration
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Your Timezone:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {user?.timezone || 'Not Set'}
                </Typography>
                {user?.timezone && (
                  <Typography variant="caption" color="text.secondary">
                    {formatTimezoneDisplay(user.timezone)}
                  </Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                  Current Time in Your Timezone:
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {user?.timezone ? 
                    currentTime.toLocaleString('en-US', { 
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true,
                      timeZone: user.timezone
                    }) : 'N/A'
                  }
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Schedule Information */}
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
              Today's Schedule Information
            </Typography>
            {todaySchedulesLoading ? (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} />
                <Typography variant="body2">Loading schedule information...</Typography>
              </Box>
            ) : !todaySchedulesData?.data?.data || (Array.isArray(todaySchedulesData.data.data) && todaySchedulesData.data.data.length === 0) ? (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  No schedules are assigned for you today.
                </Typography>
              </Alert>
            ) : (
              <Box>
                {(Array.isArray(todaySchedulesData.data.data) ? todaySchedulesData.data.data : []).map((schedule: any) => (
                  <Box key={schedule.id} sx={{ 
                    mb: 2, 
                    p: 2, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      {schedule.campaign?.name || 'Unknown Campaign'}
                    </Typography>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="primary" fontWeight="bold">
                          Local Time ({formatTimezoneDisplay(schedule.userTimezone)})
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          Start: {schedule.startTimeLocal}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          End: {schedule.endTimeLocal}
                        </Typography>
                      </Box>
                      
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          UTC Time
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          Start: {schedule.startTimeUTCFormatted || 'Invalid Date'} UTC
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          End: {schedule.endTimeUTCFormatted || 'Invalid Date'} UTC
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
      )}
    </Container>
  );
};

export default ClockInOutPage;
